import { clsx } from '@alexis/helpers/clsx';
import { dateApiFormat } from '@alexis/helpers/date';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';

import { getTranslationsState } from '@redux/selectors';

import styles from './Breadcrumbs.module.scss';

export interface BreadcrumbsProps {
  className?: string;
  hotel: Hotel | undefined;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ className, hotel }): JSX.Element => {
  const translations = useSelector(getTranslationsState);
  const { pathname } = useLocation();
  const { checkInDate, checkOutDate } = useParams<{ checkInDate: string; checkOutDate: string }>();

  const breadcrumbPaths = useMemo(() => {
    const paths = [{ label: translations.home, to: '/' }];

    const tempLocaleParam = pathname.split('/')[1];
    const localeParam = /^(?:[a-zA-Z]{2}|[a-zA-Z]{2}\-[a-zA-Z]{2})$/i.test(tempLocaleParam)
      ? tempLocaleParam
      : '';
    const productParam = pathname.split('/')[!!localeParam ? 2 : 1];

    const today = dateApiFormat(new Date());
    const tomorrow = dateApiFormat(new Date(new Date().setDate(new Date().getDate() + 1)));

    const effectiveCheckInDate = checkInDate || today;
    const effectiveCheckOutDate = checkOutDate || tomorrow;

    const buildPath = (suffix: string): string =>
      `/${localeParam}${!!localeParam ? `/${productParam}` : productParam}${suffix}`;

    if (hotel?.city?.code) {
      paths.push({
        label: hotel.city.name,
        to: buildPath(
          `/searches/${hotel.city.code.toLocaleLowerCase()}/${effectiveCheckInDate}/${effectiveCheckOutDate}`,
        ),
      });
    }

    if (hotel?.district?.id && hotel?.city?.code) {
      paths.push({
        label: hotel.district.name,
        to: buildPath(
          `/searches/${hotel.city.code.toLocaleLowerCase()}/${effectiveCheckInDate}/${effectiveCheckOutDate}/d${
            hotel.district.id
          }`,
        ),
      });
    }

    if (hotel?.name) {
      paths.push({ label: hotel.name, to: `/` });
    }

    return paths;
  }, [hotel, pathname, translations, checkInDate, checkOutDate]);

  return (
    <nav aria-label='breadcrumb' className={clsx(styles.breadcrumbs, className)}>
      <ol className={styles.list}>
        {breadcrumbPaths.map((path, index) => (
          <li
            key={index}
            className={`${styles.item} ${
              index === breadcrumbPaths.length - 1 ? styles.activeItem : ''
            }`}
          >
            {index !== breadcrumbPaths.length - 1 ? (
              <>
                <Link to={path.to} className={styles.link} target='_blank'>
                  {path.label}
                </Link>
                <span className={styles.separator}>›</span>
              </>
            ) : (
              <span className={styles.active}>{path.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
